import {
  aiLanguagePracticePages,
  getAiTutorPath,
  getSpeakingPracticePath,
} from "./aiLanguagePracticePages";
import { isHandwrittenBetweenLessonBlogSlug } from "./blogArticleSeo";
import {
  type DuolingoAlternativePage,
  duolingoAlternativePages,
  getDuolingoAlternativePagePath,
} from "./duolingoAlternativePages";
import { fillInTheGapLanguagePages } from "./fillInTheGapLanguagePages";

export type BlogRelatedPracticeLink = {
  label: string;
  href: string;
  description: string;
};

const topicSpecificRelatedLinks: Record<string, BlogRelatedPracticeLink[]> = {
  "10-practical-activities-for-effective-chinese-pronunciation-practice": [
    {
      label: "Mandarin tone practice",
      href: "/practice/mandarin-speaking",
      description: "Practice tones, rhythm, and corrected short answers in a live Mandarin loop.",
    },
    {
      label: "AI Mandarin pronunciation tutor",
      href: "/ai/mandarin-tutor",
      description: "Ask for tone-aware Mandarin corrections while the conversation keeps moving.",
    },
  ],
  "dutch-inburgering-speaking-practice": [
    {
      label: "Dutch level test",
      href: "/test-your-level/dutch",
      description: "Check your current Dutch level before choosing inburgering speaking drills.",
    },
    {
      label: "Dutch speaking practice",
      href: "/practice/dutch-speaking",
      description: "Run practical Dutch roleplays with focused correction and repetition.",
    },
  ],
  "dutch-naturalisation-language-requirement-speaking-plan": [
    {
      label: "Dutch level test",
      href: "/test-your-level/dutch",
      description: "Use the Dutch level check before building a naturalisation practice routine.",
    },
    {
      label: "AI Dutch tutor",
      href: "/ai/dutch-tutor",
      description: "Practice Dutch appointments, documents, and daily-life questions with AI.",
    },
  ],
  "cefr-a1-speaking-test-practice": [
    {
      label: "Test your language level",
      href: "/test-your-level",
      description: "Pick a target language and check whether A1 or A2 practice fits better.",
    },
    {
      label: "Browse speaking practice",
      href: "/practice",
      description: "Choose language-specific speaking routes for beginner conversation drills.",
    },
  ],
  "cefr-a2-speaking-test-practice": [
    {
      label: "Test your language level",
      href: "/test-your-level",
      description: "Use the level-test hub to choose the right A2 speaking-practice path.",
    },
    {
      label: "Browse AI tutors",
      href: "/ai",
      description: "Find AI tutor pages for language-specific A2 roleplay and correction loops.",
    },
  ],
  "ib-language-ab-initio-oral-practice": [
    {
      label: "Browse speaking practice",
      href: "/practice",
      description: "Choose a language and turn IB oral themes into live speaking repetitions.",
    },
    {
      label: "Browse AI tutors",
      href: "/ai",
      description: "Use language-specific AI tutor pages for ab initio follow-up questions.",
    },
  ],
  "nt2-dutch-speaking-practice": [
    {
      label: "Dutch level test",
      href: "/test-your-level/dutch",
      description: "Check your Dutch level before choosing NT2 conversation scenarios.",
    },
    {
      label: "Dutch speaking practice",
      href: "/practice/dutch-speaking",
      description: "Practice NT2 appointments, work, school, and daily-life conversations.",
    },
  ],
  "a2-dutch-speaking-test-practice": [
    {
      label: "Dutch level test",
      href: "/test-your-level/dutch",
      description: "Use the Dutch level check before focusing on A2 speaking prompts.",
    },
    {
      label: "AI Dutch tutor",
      href: "/ai/dutch-tutor",
      description: "Run A2 Dutch roleplays with focused correction and repeated answers.",
    },
  ],
  "goethe-a1-speaking-practice": [
    {
      label: "German level test",
      href: "/test-your-level/german",
      description: "Check whether A1 or A2 German speaking practice is the better fit.",
    },
    {
      label: "German speaking practice",
      href: "/practice/german-speaking",
      description: "Practice short German introductions, questions, requests, and repairs.",
    },
  ],
  "goethe-b1-speaking-practice": [
    {
      label: "German level test",
      href: "/test-your-level/german",
      description: "Check your German level before moving into B1-style speaking control.",
    },
    {
      label: "AI German tutor",
      href: "/ai/german-tutor",
      description: "Practice B1 German planning, presentations, follow-ups, and disagreement.",
    },
  ],
  "delf-a2-oral-practice": [
    {
      label: "French level test",
      href: "/test-your-level/french",
      description: "Check your French level before choosing DELF A2-style oral drills.",
    },
    {
      label: "French speaking practice",
      href: "/practice/french-speaking",
      description: "Practice French roleplays, repair phrases, and oral follow-up questions.",
    },
  ],
  "dele-a2-oral-practice": [
    {
      label: "Spanish level test",
      href: "/test-your-level/spanish",
      description: "Check your Spanish level before working through DELE A2 oral tasks.",
    },
    {
      label: "Spanish speaking practice",
      href: "/practice/spanish-speaking",
      description: "Practice Spanish roleplays, photo descriptions, and task-based replies.",
    },
  ],
  "ielts-speaking-practice-with-ai": [
    {
      label: "English level test",
      href: "/test-your-level/english",
      description: "Check your English level before choosing IELTS-style speaking practice.",
    },
    {
      label: "English speaking practice",
      href: "/practice/english-speaking",
      description: "Practice IELTS follow-ups, opinions, and spoken repair loops in English.",
    },
  ],
  "cambridge-a2-speaking-practice": [
    {
      label: "English level test",
      href: "/test-your-level/english",
      description: "Check whether A2 English speaking practice fits your current level.",
    },
    {
      label: "AI English tutor",
      href: "/ai/english-tutor",
      description: "Practice A2 English questions, partner replies, and corrected repeats.",
    },
  ],
  "ib-spanish-ab-initio-oral-practice": [
    {
      label: "Spanish speaking practice",
      href: "/practice/spanish-speaking",
      description: "Turn IB Spanish oral themes into live image-style speaking practice.",
    },
    {
      label: "AI Spanish tutor",
      href: "/ai/spanish-tutor",
      description: "Practice Spanish ab initio follow-ups with one correction at a time.",
    },
  ],
  "ib-french-ab-initio-oral-practice": [
    {
      label: "French speaking practice",
      href: "/practice/french-speaking",
      description: "Turn IB French visual prompts into flexible spoken answers.",
    },
    {
      label: "AI French tutor",
      href: "/ai/french-tutor",
      description: "Practice French ab initio theme links, personal examples, and follow-ups.",
    },
  ],
  "adult-language-learning-speaking-practice": [
    {
      label: "Browse speaking practice",
      href: "/practice",
      description: "Choose a language and turn adult-life situations into spoken practice.",
    },
    {
      label: "Browse AI tutors",
      href: "/ai",
      description: "Use AI tutor pages for low-pressure adult conversation practice.",
    },
  ],
  "learn-a-language-after-40-speaking-plan": [
    {
      label: "Test your language level",
      href: "/test-your-level",
      description: "Check your current level before building an after-40 speaking routine.",
    },
    {
      label: "Browse speaking practice",
      href: "/practice",
      description: "Find language-specific speaking routes for practical adult scenarios.",
    },
  ],
  "language-learning-for-busy-adults": [
    {
      label: "Browse AI tutors",
      href: "/ai",
      description: "Start a short AI speaking session around one realistic adult-life scene.",
    },
    {
      label: "Browse speaking practice",
      href: "/practice",
      description: "Pick a language and use a 15-minute speaking routine with real scenarios.",
    },
  ],
  "workplace-language-speaking-practice": [
    {
      label: "Browse speaking practice",
      href: "/practice",
      description: "Practice workplace introductions, clarification, and meeting language.",
    },
    {
      label: "Browse AI tutors",
      href: "/ai",
      description: "Use an AI tutor for workplace roleplays with focused correction.",
    },
  ],
  "conversation-practice-for-shy-adults": [
    {
      label: "Browse AI tutors",
      href: "/ai",
      description: "Practice privately with AI before trying a real-world conversation.",
    },
    {
      label: "Browse speaking practice",
      href: "/practice",
      description: "Choose low-pressure speaking routes for short conversation reps.",
    },
  ],
  "how-to-practice-speaking-alone-adult-language-learner": [
    {
      label: "Browse AI tutors",
      href: "/ai",
      description: "Use AI roleplays when you do not have a speaking partner available.",
    },
    {
      label: "Test your language level",
      href: "/test-your-level",
      description: "Check your level before choosing solo speaking drills.",
    },
  ],
};

function normalizeLanguageName(languageName: string): string {
  return languageName
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\([^)]*\)/g, "")
    .replace(/^Latin American\s+/i, "")
    .replace(/&/g, " and ")
    .replace(/['\u2019]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function findToolLanguagePage(languageName: string) {
  const normalizedLanguage = normalizeLanguageName(languageName);
  if (!normalizedLanguage || normalizedLanguage === "all-languages") return null;

  const exactMatch = fillInTheGapLanguagePages.find(
    (entry) => normalizeLanguageName(entry.name) === normalizedLanguage
  );

  if (exactMatch) return exactMatch;

  return (
    fillInTheGapLanguagePages.find((entry) => {
      const normalizedEntryName = normalizeLanguageName(entry.name);
      return (
        normalizedEntryName.startsWith(`${normalizedLanguage}-`) ||
        normalizedLanguage.startsWith(`${normalizedEntryName}-`)
      );
    }) ?? null
  );
}

function findAiPracticePage(languageName: string) {
  const normalizedLanguage = normalizeLanguageName(languageName);
  const aiSlug =
    normalizedLanguage === "mandarin-chinese" || normalizedLanguage === "chinese"
      ? "mandarin"
      : normalizedLanguage;

  return (
    aiLanguagePracticePages.find((entry) => normalizeLanguageName(entry.name) === aiSlug) ?? null
  );
}

function findDuolingoAlternativePage(languageName: string): DuolingoAlternativePage | null {
  const normalizedLanguage = normalizeLanguageName(languageName);

  return (
    duolingoAlternativePages.find(
      (entry) => normalizeLanguageName(entry.languageName) === normalizedLanguage
    ) ?? null
  );
}

function pushUniqueLink(links: BlogRelatedPracticeLink[], link: BlogRelatedPracticeLink) {
  if (links.some((existing) => existing.href === link.href)) return;
  links.push(link);
}

export function getBlogRelatedPracticeLinks(
  languageName: string,
  contextSlug?: string
): BlogRelatedPracticeLink[] {
  const toolLanguage = findToolLanguagePage(languageName);
  const aiPracticePage = findAiPracticePage(languageName);
  const duolingoAlternativePage = findDuolingoAlternativePage(languageName);
  const topicLinks = contextSlug ? (topicSpecificRelatedLinks[contextSlug] ?? []) : [];
  const betweenLessonLinks =
    contextSlug && isHandwrittenBetweenLessonBlogSlug(contextSlug)
      ? [
          {
            label: "Between-lesson speaking workflow",
            href: "/between-lessons/lesson-materials",
            description: "Turn the same class topic into a three-day or seven-day speaking cycle.",
          },
        ]
      : [];

  if (
    !toolLanguage &&
    !aiPracticePage &&
    !duolingoAlternativePage &&
    topicLinks.length === 0 &&
    betweenLessonLinks.length === 0
  ) {
    return [
      {
        label: "Browse speaking practice",
        href: "/practice",
        description: "Find language-specific speaking pages and conversation practice routes.",
      },
      {
        label: "Browse AI tutors",
        href: "/ai",
        description: "Compare AI tutor pages built around low-pressure spoken practice.",
      },
      {
        label: "Browse learning pages",
        href: "/learn",
        description: "Start from the indexed learn hub and choose a target language.",
      },
    ];
  }

  const languageLabel = toolLanguage?.name ?? aiPracticePage?.name ?? languageName;
  const links: BlogRelatedPracticeLink[] = [];

  for (const link of betweenLessonLinks) {
    pushUniqueLink(links, link);
  }

  for (const link of topicLinks) {
    pushUniqueLink(links, link);
  }

  if (duolingoAlternativePage) {
    pushUniqueLink(links, {
      label: `Duolingo alternatives for ${duolingoAlternativePage.languageName}`,
      href: getDuolingoAlternativePagePath(duolingoAlternativePage),
      description: `Compare app lessons with live ${duolingoAlternativePage.languageName} speaking practice.`,
    });
  }

  if (toolLanguage) {
    for (const link of [
      {
        label: `Learn ${languageLabel}`,
        href: `/learn/${toolLanguage.slug}`,
        description: `Open ChickyTutor with ${languageLabel} selected for a speaking session.`,
      },
      {
        label: `${languageLabel} writing practice`,
        href: `/tools/writing-practice-language-learning/${toolLanguage.slug}`,
        description: `Turn article vocabulary into corrected ${languageLabel} sentences.`,
      },
      {
        label: `${languageLabel} fill-in-the-gap`,
        href: `/tools/fill-in-the-gap-language-practice/${toolLanguage.slug}`,
        description: `Practice ${languageLabel} vocabulary and grammar with visual gap drills.`,
      },
    ]) {
      pushUniqueLink(links, link);
    }
  }

  if (aiPracticePage) {
    for (const link of [
      {
        label: `AI ${aiPracticePage.name} tutor`,
        href: getAiTutorPath(aiPracticePage),
        description: `Use AI tutor prompts for ${aiPracticePage.name} corrections and replies.`,
      },
      {
        label: `${aiPracticePage.name} speaking practice`,
        href: getSpeakingPracticePath(aiPracticePage),
        description: `Practice spoken ${aiPracticePage.name} with focused conversation loops.`,
      },
    ]) {
      pushUniqueLink(links, link);
    }
  }

  return links;
}
